import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { BodyText } from '@block-explorer/ui';
import {
  ContractResult,
  ContractTypeId,
  DataResponse,
  Deploy,
  DeployTransferResult,
  FTActionsResult,
  FTActionType,
  FTTransactionResult,
  GetDeployResult,
  NftActionsResult,
  NftTypeToEntryPointMap,
} from '@block-explorer/entities';
import { FlexColumn, FlexRow } from '@make-software/cspr-design';
import { getDeployStatus, Status } from '../deploy-status/deploy-status';
import DeployActionAssociatedKeys from './components/DeployActionAssociatedKeys';
import { DeployResultNft } from './components/DeployResultNft';
import { DeployResultCep18 } from './components/DeployResultCep18';
import TransferActionRow from './components/TransferActionRow';
import ExpandCollapsedButton from '../expand-collapsed/expand-collapsed-button';
import { ActionIdentificationHashesType } from './deploy-action-row';
import { DeployActionDataProvider } from './services/deploy-action-context';
import { isTransferDeploy } from '../../utils/contract';
import { isNonNullable } from '../../utils/guards';

const DefaultResultItem = styled(FlexRow)(({ theme }) => ({
  padding: '14px 0',
  ':not(:first-child):after': {
    content: "''",
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    borderBottom: theme.border.tableRowSeparator,
  },

  position: 'relative',
}));

const GrayResultItem = styled(FlexRow)(({ theme }) => ({
  borderRadius: '4px',
  background: `${theme.styleguideColors.backgroundSecondary}73`,
  padding: '10px 16px',
  position: 'relative',
  span: {
    fontSize: '13px',
  },
  '> *': {
    flexWrap: 'wrap',
  },
  ':not(:first-child)': {
    marginTop: '8px',
  },
}));

const ResultItemWrapper = ({ variation, ...props }) => {
  return variation === ResultRowVariation.default ? (
    <DefaultResultItem {...props} />
  ) : (
    <GrayResultItem {...props} />
  );
};

const MAXIMUM_VISIBLE_ROWS = 3;

enum DeployResultRowType {
  'NFT' = 1,
  'FT' = 2,
  'UNI' = 3,
  'LS' = 4,
  'NativeTransfer' = 5,
}

export enum ResultRowVariation {
  default = 'default',
  gray = 'gray',
}

interface DeployResultRowComponentProps {
  deploy: Deploy;
  loading: boolean;
  actionIdentificationHashes: ActionIdentificationHashesType;
  deployRawData?: DataResponse<GetDeployResult> | null;
  actionComponents?: React.ReactElement[] | null;
  variation?: ResultRowVariation;
  shouldCollapse?: boolean;
}

interface ResultAction {
  component: JSX.Element;
  hideInCollapsedView: boolean;
}

const isPurseToPurseTransfer = (transfer: DeployTransferResult) =>
  Boolean(
    transfer.from_purse &&
      transfer.to_purse &&
      !transfer.from_purse_public_key &&
      !transfer.to_purse_public_key &&
      !transfer.to_account_hash
  );

const getDuplicatedPurseToPurseTransferAmounts = (
  transfers: DeployTransferResult[]
) => {
  const amountsCount = transfers.reduce((acc, transfer) => {
    acc.set(transfer.amount, (acc.get(transfer.amount) || 0) + 1);
    return acc;
  }, new Map<string, number>());

  return Array.from(amountsCount.entries()).reduce((acc, [amount, count]) => {
    if (count > 1) {
      acc.add(amount);
    }

    return acc;
  }, new Set<string>());
};

const sortActionsByTypeAndOrder = (deploy: Deploy) => {
  const nftActions: NftActionsResult[] = (deploy.nftActions || []).map(
    (action) => ({
      ...action,
      type: DeployResultRowType.NFT,
    })
  );
  const ftActions: FTActionsResult[] = (deploy.ftActions || []).map(
    (action) => ({
      ...action,
      type: DeployResultRowType.FT,
    })
  );

  const sortedTransfers: DeployTransferResult[] = (deploy.transfers || [])
    .sort(
      (transferA, transferB) =>
        transferA.transfer_index - transferB.transfer_index
    )
    .map((action) => ({
      ...action,
      type: DeployResultRowType.NativeTransfer,
    }));

  const sortedTokenActions = [...nftActions, ...ftActions].sort(
    (actionA, actionB) => actionA.transform_idx - actionB.transform_idx
  );

  return [...sortedTransfers, ...sortedTokenActions];
};

const getSortedResultActions = ({
  deploy,
  deployRawData,
  actionIdentificationHashes,
  actionComponents,
}): ResultAction[] => {
  const sortedActions = sortActionsByTypeAndOrder(deploy);
  const duplicatedPurseToPurseTransferAmounts =
    getDuplicatedPurseToPurseTransferAmounts(deploy.transfers || []);

  const getActionElementToRender = (action) => {
    switch (action.type) {
      case DeployResultRowType.NFT:
        return {
          component: (
            <DeployResultNft key={action.transform_idx} nftAction={action} />
          ),
          hideInCollapsedView: false,
        };
      case DeployResultRowType.FT:
        return {
          component: (
            <DeployResultCep18
              key={action.transform_idx}
              ftAction={action as FTTransactionResult}
            />
          ),
          hideInCollapsedView: false,
        };
      case DeployResultRowType.NativeTransfer:
        return {
          component: (
            <TransferActionRow
              key={action.transfer_index}
              loading={false}
              timeTransactionCurrencyRate={deploy.timeTransactionCurrencyRate}
              transfer={action}
              actionPoolAccountHash={
                actionIdentificationHashes.auction_pool_account_hash
              }
              renderAsResultAction
            />
          ),
          hideInCollapsedView:
            isPurseToPurseTransfer(action) &&
            duplicatedPurseToPurseTransferAmounts.has(action.amount),
        };
      default:
        return null;
    }
  };

  const associatedKeyComponent =
    deploy.contractHash ===
    actionIdentificationHashes.associated_keys_contract_hash
      ? {
          component: (
            <DeployActionAssociatedKeys
              deploy={deploy}
              deployRawData={deployRawData}
              renderAsResultAction
            />
          ),
          hideInCollapsedView: false,
        }
      : null;

  const sortedResultActions = sortedActions.map((action) =>
    getActionElementToRender(action)
  );

  return [
    associatedKeyComponent,
    ...sortedResultActions,
    ...(actionComponents || []).map((component) => ({
      component,
      hideInCollapsedView: false,
    })),
  ].filter(isNonNullable);
};

const manageCollapsingResults = ({
  deploy,
  actionIdentificationHashes,
  actionsArrayLength,
  shouldCollapse,
}) => {
  if (!shouldCollapse || actionsArrayLength > 1) return false;

  const contractTypeId =
    deploy.contractPackage?.latest_version_contract_type_id ||
    deploy.contractPackage?.contract_type_id;
  const isCep18 =
    contractTypeId === ContractTypeId.CustomCep18 ||
    contractTypeId === ContractTypeId.Cep18;

  if (isCep18) {
    const cep18ActionName = deploy?.entryPoint?.name.toLowerCase() || '';
    const cep18ResultName =
      deploy.ftActions && deploy.ftActions[0]
        ? FTActionType[deploy.ftActions[0].ft_action_type_id].toLowerCase()
        : null;
    return cep18ActionName === cep18ResultName;
  }

  const isNft =
    contractTypeId === ContractTypeId.CEP78Nft ||
    contractTypeId === ContractTypeId.CEP47Nft ||
    contractTypeId === ContractTypeId.CustomCEP78Nft ||
    contractTypeId === ContractTypeId.CustomCEP47Nft ||
    contractTypeId === ContractTypeId.CEP95Nft;

  if (isNft) {
    const nftActionName = deploy?.entryPoint?.name || '';
    const nftResultName =
      deploy.nftActions && deploy.nftActions[0]
        ? NftTypeToEntryPointMap[deploy.nftActions[0].nft_action_id]
        : null;
    return nftActionName === nftResultName;
  }

  const isTransfer = isTransferDeploy(
    deploy.contractHash,
    deploy.entryPoint?.name
  );

  const isAssociatedKeysDeploy =
    deploy.contractHash ===
    actionIdentificationHashes.associated_keys_contract_hash;

  return isTransfer || isAssociatedKeysDeploy;
};

const DeployResultRowComponent = (props: DeployResultRowComponentProps) => {
  const { t } = useTranslation();
  const {
    deploy,
    actionIdentificationHashes,
    deployRawData,
    variation = ResultRowVariation.default,
    shouldCollapse = false,
    actionComponents,
  } = props;

  const allSortedActions = getSortedResultActions({
    deploy,
    deployRawData,
    actionComponents,
    actionIdentificationHashes,
  });

  const actionsCount = allSortedActions?.length ?? 0;
  const isSingleResult = actionsCount <= 1;

  const [isCollapsed, setCollapsed] = useState<boolean>(shouldCollapse);

  const handleExpandList = () => {
    setCollapsed(!isCollapsed);
  };

  const shouldCollapseDuplicatedResults = manageCollapsingResults({
    deploy,
    actionIdentificationHashes,
    actionsArrayLength: allSortedActions.length,
    shouldCollapse,
  });

  useEffect(() => {
    if (shouldCollapseDuplicatedResults) setCollapsed(true);
  }, [shouldCollapseDuplicatedResults]);

  const maxVisibleRows = shouldCollapseDuplicatedResults
    ? 0
    : MAXIMUM_VISIBLE_ROWS;

  const visibleSortedActions = allSortedActions
    .filter((action) => !action.hideInCollapsedView)
    .slice(0, maxVisibleRows);

  const showCollapsedButton =
    (shouldCollapse || shouldCollapseDuplicatedResults) &&
    actionsCount > visibleSortedActions.length;

  const visibleActions = isCollapsed ? visibleSortedActions : allSortedActions;

  const collapsedLabel = (
    <>
      <Trans t={t}>{isSingleResult ? 'View ' : 'View all '}</Trans>
      {actionsCount}
      <Trans t={t}>{isSingleResult ? ' result' : ' results'}</Trans>
    </>
  );

  const expandedLabel = isSingleResult
    ? t('Collapse result')
    : t('Collapse results');

  return (
    <FlexColumn>
      {actionsCount
        ? visibleActions.map((action, idx) => (
            <ResultItemWrapper key={`actions-${idx}`} variation={variation}>
              {action.component}
            </ResultItemWrapper>
          ))
        : null}
      {showCollapsedButton ? (
        <ExpandCollapsedButton
          collapsedLabel={collapsedLabel}
          expandedLabel={expandedLabel}
          onExpand={handleExpandList}
          hasTopMargin={false}
        />
      ) : null}
    </FlexColumn>
  );
};

type DeployResultRowProps = DeployResultRowComponentProps & {
  getAccountInfo: <T>(publicKey: string) => T | null | undefined;
  getContractInfoByHash?: (
    contractHash: string
  ) => ContractResult | null | undefined;
  getContractPackagePath: (hash: string) => string | null;
};

export const DeployResultRow = (props: DeployResultRowProps) => {
  const {
    getAccountInfo,
    getContractInfoByHash,
    getContractPackagePath,
    ...rest
  } = props;
  const deployStatus = getDeployStatus(props.deploy);

  if (deployStatus === Status.Error) {
    return null;
  }

  if (props.loading) {
    return (
      <ResultItemWrapper variation={props.variation}>
        <BodyText size={3}>
          <Skeleton />
        </BodyText>
      </ResultItemWrapper>
    );
  }

  const showResultRow =
    props.deploy.transfers ||
    props.deploy.nftActions ||
    props.deploy.ftActions ||
    props.deploy.contractHash ===
      props.actionIdentificationHashes.associated_keys_contract_hash;

  if (!showResultRow) {
    return null;
  }

  return (
    <DeployActionDataProvider
      getAccountInfo={getAccountInfo}
      getContractInfoByHash={getContractInfoByHash}
      getContractPackagePath={getContractPackagePath}
    >
      <DeployResultRowComponent {...rest} />
    </DeployActionDataProvider>
  );
};
